import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiSearch, FiFilter, FiEye, FiEdit, FiTrash2, FiPackage, FiTag, FiCreditCard } from 'react-icons/fi';
import { formatRandSimple, formatRandNoDecimals } from '../../utils/currency';
import styles from './ProductList.module.css';

interface Product {
  id: string;
  name: string;
  description: string;
  sku: string;
  category: string;
  price: number;
  cost: number;
  stock: number;
  unit: string;
  status: 'active' | 'inactive' | 'out_of_stock';
  tags: string[];
  image?: string;
  createdAt: string;
  updatedAt: string;
}

// Mock data
const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Basic Website',
    description: 'Professional basic website with responsive design',
    sku: 'WEB-BASIC-001',
    category: 'Websites',
    price: 1499,
    cost: 899,
    stock: 999,
    unit: 'project',
    status: 'active',
    tags: ['Website', 'Basic', 'Responsive'],
    createdAt: '2024-01-01',
    updatedAt: '2024-01-15'
  },
  {
    id: '2',
    name: 'Standard Website',
    description: 'Advanced website with custom features and CMS',
    sku: 'WEB-STD-001',
    category: 'Websites',
    price: 2499,
    cost: 1499,
    stock: 999,
    unit: 'project',
    status: 'active',
    tags: ['Website', 'Standard', 'CMS'],
    createdAt: '2024-01-05',
    updatedAt: '2024-01-10'
  },
  {
    id: '3',
    name: 'Corporate Pack',
    description: 'Premium corporate website with advanced functionality',
    sku: 'WEB-CORP-001',
    category: 'Websites',
    price: 3499,
    cost: 2099,
    stock: 999,
    unit: 'project',
    status: 'active',
    tags: ['Website', 'Corporate', 'Premium'],
    createdAt: '2024-01-10',
    updatedAt: '2024-01-20'
  },
  {
    id: '4',
    name: 'Starter eCommerce',
    description: 'Basic eCommerce website with essential features',
    sku: 'ECO-START-001',
    category: 'eCommerce',
    price: 5499,
    cost: 3299,
    stock: 999,
    unit: 'project',
    status: 'active',
    tags: ['eCommerce', 'Starter', 'Online Store'],
    createdAt: '2024-01-15',
    updatedAt: '2024-01-25'
  },
  {
    id: '5',
    name: 'Standard eCommerce',
    description: 'Full-featured eCommerce platform with advanced tools',
    sku: 'ECO-STD-001',
    category: 'eCommerce',
    price: 7999,
    cost: 4799,
    stock: 999,
    unit: 'project',
    status: 'active',
    tags: ['eCommerce', 'Standard', 'Advanced'],
    createdAt: '2024-01-20',
    updatedAt: '2024-01-30'
  },
  {
    id: '6',
    name: 'Hosting',
    description: 'Monthly web hosting service with SSL and support',
    sku: 'HOST-MONTH-001',
    category: 'Hosting',
    price: 439,
    cost: 219,
    stock: 999,
    unit: 'month',
    status: 'active',
    tags: ['Hosting', 'Monthly', 'SSL'],
    createdAt: '2024-01-25',
    updatedAt: '2024-02-01'
  },
  {
    id: '7',
    name: 'Domain .co.za',
    description: 'South African domain registration (.co.za)',
    sku: 'DOM-COZA-001',
    category: 'Domains',
    price: 129,
    cost: 89,
    stock: 999,
    unit: 'year',
    status: 'active',
    tags: ['Domain', 'South Africa', '.co.za'],
    createdAt: '2024-01-30',
    updatedAt: '2024-02-05'
  },
  {
    id: '8',
    name: 'Domain .com',
    description: 'International domain registration (.com)',
    sku: 'DOM-COM-001',
    category: 'Domains',
    price: 189,
    cost: 129,
    stock: 999,
    unit: 'year',
    status: 'active',
    tags: ['Domain', 'International', '.com'],
    createdAt: '2024-02-01',
    updatedAt: '2024-02-10'
  },
  {
    id: '9',
    name: 'Domain .org',
    description: 'Organization domain registration (.org)',
    sku: 'DOM-ORG-001',
    category: 'Domains',
    price: 199,
    cost: 139,
    stock: 999,
    unit: 'year',
    status: 'active',
    tags: ['Domain', 'Organization', '.org'],
    createdAt: '2024-02-05',
    updatedAt: '2024-02-15'
  },
  {
    id: '10',
    name: 'Domain .org.za',
    description: 'South African organization domain (.org.za)',
    sku: 'DOM-ORGZA-001',
    category: 'Domains',
    price: 165,
    cost: 115,
    stock: 999,
    unit: 'year',
    status: 'active',
    tags: ['Domain', 'South African', '.org.za'],
    createdAt: '2024-02-10',
    updatedAt: '2024-02-20'
  }
];

const categories = ['All', 'Websites', 'eCommerce', 'Hosting', 'Domains'];
const statuses = ['All', 'active', 'inactive', 'out_of_stock'];

export function ProductList() {
  const [products, setProducts] = useState<Product[]>(mockProducts);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'All' || product.category === categoryFilter;
    const matchesStatus = statusFilter === 'All' || product.status === statusFilter;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      setProducts(products.filter(product => product.id !== id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedProducts.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} product(s)?`)) {
      setProducts(products.filter(product => !selectedProducts.includes(product.id)));
      setSelectedProducts([]);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProducts(filteredProducts.map(product => product.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, id]);
    } else {
      setSelectedProducts(selectedProducts.filter(productId => productId !== id));
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      active: styles.statusActive,
      inactive: styles.statusInactive,
      out_of_stock: styles.statusOutOfStock
    };
    return `${styles.statusBadge} ${statusClasses[status as keyof typeof statusClasses]}`;
  };

  const getStatusText = (status: string) => {
    return status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1);
  };

  const calculateProfit = (price: number, cost: number) => {
    return ((price - cost) / cost * 100).toFixed(1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Products</h1>
          <p className={styles.subtitle}>Manage your product catalog and pricing</p>
        </div>
        <Link to="/products/create" className={styles.createButton}>
          <FiPlus size={16} />
          New Product
        </Link>
      </div>

      {/* Filters and Search */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filterGroup}>
          <FiFilter className={styles.filterIcon} />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.filterSelect}
          >
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filterSelect}
          >
            {statuses.map(status => (
              <option key={status} value={status}>
                {status === 'All' ? 'All Status' : getStatusText(status)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.viewToggle}>
          <button
            className={`${styles.viewButton} ${viewMode === 'grid' ? styles.active : ''}`}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </button>
          <button
            className={`${styles.viewButton} ${viewMode === 'list' ? styles.active : ''}`}
            onClick={() => setViewMode('list')}
          >
            List
          </button>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedProducts.length > 0 && (
        <div className={styles.bulkActions}>
          <span>{selectedProducts.length} product(s) selected</span>
          <button onClick={handleBulkDelete} className={styles.bulkDeleteButton}>
            <FiTrash2 size={16} />
            Delete Selected
          </button>
        </div>
      )}

      {/* Products Display */}
      {viewMode === 'grid' ? (
        <div className={styles.productsGrid}>
          {filteredProducts.map((product) => (
            <div key={product.id} className={styles.productCard}>
              <div className={styles.cardHeader}>
                <div className={styles.productImage}>
                  {product.image ? (
                    <img src={product.image} alt={product.name} />
                  ) : (
                    <FiPackage size={32} />
                  )}
                </div>
                <div className={styles.cardActions}>
                  <input
                    type="checkbox"
                    checked={selectedProducts.includes(product.id)}
                    onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                    className={styles.checkbox}
                  />
                  <Link to={`/products/${product.id}`} className={styles.actionButton} title="View">
                    <FiEye size={16} />
                  </Link>
                  <Link to={`/products/${product.id}/edit`} className={styles.actionButton} title="Edit">
                    <FiEdit size={16} />
                  </Link>
                  <button 
                    onClick={() => handleDelete(product.id)} 
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    title="Delete"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>

              <div className={styles.productInfo}>
                <h3 className={styles.productName}>{product.name}</h3>
                <p className={styles.productDescription}>{product.description}</p>
                <div className={styles.productMeta}>
                  <span className={styles.sku}>SKU: {product.sku}</span>
                  <span className={styles.category}>{product.category}</span>
                </div>
              </div>

              <div className={styles.productStats}>
                <div className={styles.stat}>
                  <FiCreditCard className={styles.statIcon} />
                  <div>
                            <span className={styles.statLabel}>Price</span>
        <span className={styles.statValue}>{formatRandSimple(product.price)}</span>
                  </div>
                </div>
                <div className={styles.stat}>
                  <FiPackage className={styles.statIcon} />
                  <div>
                    <span className={styles.statLabel}>Stock</span>
                    <span className={styles.statValue}>{product.stock} {product.unit}</span>
                  </div>
                </div>
                <div className={styles.stat}>
                  <FiTag className={styles.statIcon} />
                  <div>
                    <span className={styles.statLabel}>Profit</span>
                    <span className={styles.statValue}>{calculateProfit(product.price, product.cost)}%</span>
                  </div>
                </div>
              </div>

              <div className={styles.productFooter}>
                <span className={getStatusBadge(product.status)}>
                  {getStatusText(product.status)}
                </span>
                {product.tags.length > 0 && (
                  <div className={styles.tags}>
                    {product.tags.slice(0, 2).map((tag, index) => (
                      <span key={index} className={styles.tag}>
                        {tag}
                      </span>
                    ))}
                    {product.tags.length > 2 && (
                      <span className={styles.tagMore}>+{product.tags.length - 2}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.productsTable}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={styles.checkbox}
                  />
                </th>
                <th>Product</th>
                <th>SKU</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => (
                <tr key={product.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                      className={styles.checkbox}
                    />
                  </td>
                  <td>
                    <div className={styles.productCell}>
                      <div className={styles.productImage}>
                        {product.image ? (
                          <img src={product.image} alt={product.name} />
                        ) : (
                          <FiPackage size={16} />
                        )}
                      </div>
                      <div>
                        <div className={styles.productName}>{product.name}</div>
                        <div className={styles.productDescription}>{product.description}</div>
                      </div>
                    </div>
                  </td>
                  <td>{product.sku}</td>
                  <td>{product.category}</td>
                  <td>{formatRandSimple(product.price)}</td>
                  <td>{product.stock} {product.unit}</td>
                  <td>
                    <span className={getStatusBadge(product.status)}>
                      {getStatusText(product.status)}
                    </span>
                  </td>
                  <td className={styles.actions}>
                    <Link to={`/products/${product.id}`} className={styles.actionButton} title="View">
                      <FiEye size={16} />
                    </Link>
                    <Link to={`/products/${product.id}/edit`} className={styles.actionButton} title="Edit">
                      <FiEdit size={16} />
                    </Link>
                    <button 
                      onClick={() => handleDelete(product.id)} 
                      className={`${styles.actionButton} ${styles.deleteButton}`}
                      title="Delete"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredProducts.length === 0 && (
        <div className={styles.emptyState}>
          <p>No products found. Add your first product to get started.</p>
          <Link to="/products/create" className={styles.createButton}>
            <FiPlus size={16} />
            Add Product
          </Link>
        </div>
      )}
    </div>
  );
}
